/**
 * Resource role admin request/response contracts
 * ───────────────────────────────────────────────
 * Zod schemas for the workspace-admin endpoints that view, add, edit and remove
 * billable resource roles (the workspace-global, per-product `resource_role`
 * catalog + its default `resource_role_description` overlay). Imported by both
 * the API (request validation) and the web Resources tab (request building /
 * typing).
 *
 * `roleKey` is server-generated (a slug of `roleName`, deduped per workspace +
 * product) and immutable - it is the stable join key the estimate runtime reads,
 * so the client never supplies or edits it. `userJobDescription` /
 * `aiFunctionalDescription` are the default (categoryKey = NULL) description;
 * `categoryDescriptions` are the per-deal-category overrides (COS-TBD
 * per-category-role-descriptions). An override replaces the default text the
 * AI reads for that category AND puts the role on that category's `ai`-plan
 * roster; no override = the category uses the default text.
 */
import { z } from 'zod';
/** One per-deal-category description override of a role. */
export const resourceRoleCategoryDescriptionSchema = z.object({
    categoryKey: z.string().trim().min(1).max(100),
    userJobDescription: z.string().nullable(),
    aiFunctionalDescription: z.string().nullable(),
});
export const resourceRoleSummarySchema = z.object({
    uuid: z.string(),
    roleKey: z.string(),
    roleName: z.string(),
    level: z.string().nullable(),
    category: z.string().nullable(),
    onshore: z.number().int(),
    offshore: z.number().int(),
    currency: z.string(),
    displayOrder: z.number().int(),
    /** Default (categoryKey = NULL) description fields, joined in. */
    userJobDescription: z.string().nullable(),
    aiFunctionalDescription: z.string().nullable(),
    /** Per-category overrides, by categoryKey (only categories that have one). */
    categoryDescriptions: z.array(resourceRoleCategoryDescriptionSchema),
});
export const createResourceRoleSchema = z.object({
    roleName: z.string().trim().min(1),
    level: z.string().trim().min(1).nullable().optional(),
    category: z.string().trim().min(1).nullable().optional(),
    onshore: z.number().int().min(0),
    offshore: z.number().int().min(0),
    currency: z.string().trim().min(1).default('USD'),
    userJobDescription: z.string().nullable().optional(),
    aiFunctionalDescription: z.string().nullable().optional(),
});
export const updateResourceRoleSchema = z.object({
    roleName: z.string().trim().min(1).optional(),
    level: z.string().trim().min(1).nullable().optional(),
    category: z.string().trim().min(1).nullable().optional(),
    onshore: z.number().int().min(0).optional(),
    offshore: z.number().int().min(0).optional(),
    currency: z.string().trim().min(1).optional(),
    displayOrder: z.number().int().min(0).optional(),
    userJobDescription: z.string().nullable().optional(),
    aiFunctionalDescription: z.string().nullable().optional(),
    /**
     * Per-category overrides to write. Each entry replaces that category's override; both
     * fields empty/null = remove it (the category goes back to the default). Categories not
     * listed are left as they are. A category the product does not have is a 400.
     */
    categoryDescriptions: z.array(resourceRoleCategoryDescriptionSchema).max(50).optional(),
});
// ── Staffing coverage + set-by-key (COS-TBD mcp-missing-staffing) ──────────────
//
// Unlike the admin create above, these address roles by the EXACT key the
// resource-plan profile staffs (`lead-consultant`), never a slug of a display name.
// Matching treats `-` and `_` as equal (`canonicalRoleKey`), so a legacy
// `lead_consultant` row is updated in place, not duplicated.
/** One role in a set-rates request. Rates are whole currency units per hour. */
export const setResourceRateRowSchema = z.object({
    roleKey: z.string().trim().min(1).max(100),
    /** Required only when creating a role no rate card names. */
    roleName: z.string().trim().min(1).max(200).optional(),
    onshore: z.number().int().min(0),
    offshore: z.number().int().min(0),
    /** Defaults to the product's existing currency, else USD. */
    currency: z.string().trim().min(1).max(10).optional(),
});
export const setResourceRatesSchema = z.object({
    roles: z.array(setResourceRateRowSchema).min(1).max(50),
    /** Deal category whose resource plan defines the needed roles (defaults to the product's default plan). */
    categoryKey: z.string().trim().min(1).nullable().optional(),
});
// ── Client staffing editor (COS-TBD client-staffing-knobs) ─────────────────────
//
// A workspace admin chooses which roles staff a product's deals and how the hours
// split between them (Workspace -> Resources -> Staffing). Shares are WHOLE PERCENTS
// on the wire; the resource-plan contract stores fractions (40 -> 0.4). A role is
// "off" when its share is 0 (or absent) - there is no separate on/off store.
/** The single tier key a `flat_percentage` plan uses on the wire (it has no size tiers). */
export const STAFFING_FLAT_TIER = 'all';
/** role key -> whole-percent share of the hours (0-100). */
export const staffingShareMapSchema = z.record(z.string().trim().min(1).max(100), z.number().int().min(0).max(100));
export const saveStaffingSchema = z.object({
    /** The plan's `categoryKey` from the GET (null only for a product with no categories). */
    categoryKey: z.string().trim().min(1).nullable(),
    /**
     * Tier -> shares. `size_tiered`: one entry per size tier (small/medium/large); send the
     * same map for every tier when the split does not vary by deal size. `flat_percentage`:
     * the single key `STAFFING_FLAT_TIER`. Roles assigned by section (`overrideRoleKeys`)
     * are not part of the split and must not be sent.
     */
    shares: z.record(z.string().trim().min(1).max(50), staffingShareMapSchema),
});
//# sourceMappingURL=resource-role-admin.js.map